import { useEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { V4 } from '../tokens';
import { GA_ANNOUNCEMENT, SUNSET_ANNOUNCEMENT } from '@utils/featureRollout/strings';
import { getActiveCommerceId } from '@utils/featureRollout/cache';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

// Banner de anuncio GA (C2): cuando el comercio ya está graduado (ga_forced), la versión nueva
// es la oficial. Mostramos un aviso tipo "nueva funcionalidad", DESCARTABLE y NO intrusivo (no
// bloquea, no es modal). Copy centralizada en `featureRollout/strings.ts`. 100% inline V4.
//
// El descarte se persiste por commerce_id en localStorage (llave dedicada que resetStore NO
// borra) para no re-mostrarlo en cada navegación tras descartar. Telemetría fire-and-forget.

const DISMISS_PREFIX = 'plick:feature-ga-announce-dismissed:v1:';

// `suffix` separa el descarte del aviso GA del aviso de SUNSET (e incluye la fecha): si la fecha de
// retiro cambia, el aviso vuelve a aparecer aunque el anterior se hubiera descartado.
function dismissKey(commerceId: string, featureKey: string, suffix = ''): string {
  return `${DISMISS_PREFIX}${commerceId}:${featureKey}${suffix}`;
}

function isDismissed(featureKey: string, suffix = ''): boolean {
  if (typeof window === 'undefined') return false;
  const commerceId = getActiveCommerceId();
  if (!commerceId) return false;
  try {
    return window.localStorage.getItem(dismissKey(commerceId, featureKey, suffix)) === '1';
  } catch {
    return false;
  }
}

function persistDismissed(featureKey: string, suffix = ''): void {
  if (typeof window === 'undefined') return;
  const commerceId = getActiveCommerceId();
  if (!commerceId) return;
  try {
    window.localStorage.setItem(dismissKey(commerceId, featureKey, suffix), '1');
  } catch {
    // ignore quota / private-mode
  }
}

const MESES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

// 'YYYY-MM-DD' → '1 de julio' (parseo manual para evitar corrimientos por zona horaria).
function formatSunset(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return date;
  return `${d} de ${MESES_ES[m - 1]}`;
}

const SparkIcon: ReactNode = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path
      d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: V4.secondaryFaded,
  border: `1px solid ${V4.secondaryDark}22`,
  borderRadius: 12,
  padding: '12px 14px',
  fontFamily: 'inherit',
};

const dismissButton: CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  border: 0,
  background: 'transparent',
  color: V4.grayUltraDark,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export interface GaAnnouncementBannerProps {
  // Feature a la que aplica (para keyear el descarte y la telemetría). Default 'menu-v2'.
  featureKey: string;
  // SUNSET: si viene, el banner muestra el aviso de RETIRO de la versión vieja con esta fecha
  // ('YYYY-MM-DD') en vez del aviso GA. Descarte durable independiente (incluye la fecha en la llave).
  sunsetDate?: string;
}

export const GaAnnouncementBanner: FC<GaAnnouncementBannerProps> = ({ featureKey, sunsetDate }) => {
  const isSunset = Boolean(sunsetDate);
  const suffix = sunsetDate ? `:sunset:${sunsetDate}` : '';
  // Decisión SÍNCRONA inicial (sin parpadeo): leemos el descarte persistido al montar.
  const [visible, setVisible] = useState(() => !isDismissed(featureKey, suffix));
  const { trackEvent } = useTracking();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const shownRef = useRef(false);
  const kindParam = isSunset ? { kind: 'sunset' as const } : {};

  // Telemetría: el anuncio se mostró (una vez por montaje, solo si es visible).
  useEffect(() => {
    if (!visible || shownRef.current) return;
    shownRef.current = true;
    trackEventRef.current(TRACK_EVENT_NAMES.FeatureGaAnnouncementShown, { feature_key: featureKey, ...kindParam });
  }, [visible, featureKey]);

  if (!visible) return null;

  const title = isSunset ? SUNSET_ANNOUNCEMENT.title : GA_ANNOUNCEMENT.title;
  const body = isSunset ? SUNSET_ANNOUNCEMENT.body(formatSunset(sunsetDate as string)) : GA_ANNOUNCEMENT.body;
  const dismissAria = isSunset ? SUNSET_ANNOUNCEMENT.dismissAria : GA_ANNOUNCEMENT.dismissAria;

  const onDismiss = () => {
    persistDismissed(featureKey, suffix);
    setVisible(false);
    trackEventRef.current(TRACK_EVENT_NAMES.FeatureGaAnnouncementDismissed, { feature_key: featureKey, ...kindParam });
  };

  return (
    <div style={bannerStyle} role="status">
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: V4.white,
          color: V4.secondary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {SparkIcon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: V4.primary }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12, color: V4.grayUltraDark, marginTop: 2, lineHeight: 1.45 }}>
          {body}
        </span>
      </span>
      <button type="button" style={dismissButton} aria-label={dismissAria} onClick={onDismiss}>
        {CloseIcon}
      </button>
    </div>
  );
};

export default GaAnnouncementBanner;
